import { fromPromise } from 'xstate5';

import { type Predictions } from '@sg/garnish';

import {
  fetchPredictions,
  searchLocationsByRestaurantName,
  selectPrediction as selectPredictionService,
} from '../../../services';
import { type Event, type LocationGroups, type PositionCoordinates } from '../../../types';
import { type SearchContext } from '../locations';

import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

export const getPredictionsAndRestaurantsActor = fromPromise(
  async ({ input }: { input: PredictionsAndRestaurantsActorInput }) => {
    const { client, predictionsSearchQuery, currentPosition, shouldUseRadarForPickup, context } =
      input;

    if (!predictionsSearchQuery) {
      const emptyResult: { predictions: Predictions; locations: LocationGroups } = {
        predictions: [],
        locations: { pickup: [], outpost: [] },
      };

      return emptyResult;
    }

    const near = currentPosition
      ? `${currentPosition.latitude},${currentPosition.longitude}`
      : undefined;

    const [predictionsResponse, locations] = await Promise.all([
      fetchPredictions(predictionsSearchQuery, {
        shouldUseRadar: shouldUseRadarForPickup,
        near,
      }),
      searchLocationsByRestaurantName({ client, context }),
    ]);

    return {
      predictions: predictionsResponse?.predictions,
      locations,
    };
  },
);

export const selectPredictionActor = fromPromise(
  async ({ input }: { input: SelectPredictionActorInput }) =>
    selectPredictionService({ predictions: input.predictions, event: input.event }),
);

// ─── Types ───────────────────────────────────────────────────────────────────

export type PredictionsAndRestaurantsActorInput = {
  client: Client;
  predictionsSearchQuery: string | undefined;
  currentPosition: PositionCoordinates | undefined;
  shouldUseRadarForPickup: boolean;
  context: SearchContext;
};

export type SelectPredictionActorInput = {
  predictions: Predictions;
  event: Extract<Event, { type: 'SELECT_PREDICTION' }>;
};
